import axios from "axios";
import moment from "moment-timezone";

const api = () => {
  let axiosInst;

  return {
    initialize: (appId, siteId, viewId, keycloak) => {
      axiosInst = axios.create({
        baseURL: `${process.env.REACT_APP_DWM_URL}/api/mobile/apps/${appId}/views/${viewId}/reception`,
      });

      axiosInst.interceptors.request.use(axiosConfig => {
        axiosConfig.headers.Authorization = `Bearer ${keycloak.token}`;
        axiosConfig.headers["app-id"] = appId;
        axiosConfig.url += `&siteId=${siteId}`;

        return axiosConfig;
      }, null);
    },
    getConfig: async () => {
      try {
        const result = await axiosInst.get(`/config?`);
        return result.data;
      } catch (error) {
        console.error("Failed to ge config", { message: error.message });
        throw error;
      }
    },
    downloadStatus: async () => {
      try {
        const result = await axiosInst.get(`/status/export?`, {
          params: { date: moment().toISOString() },
          responseType: "blob",
        });

        return result.data;
      } catch (error) {
        console.error("Failed to download status", { message: error.message });
        throw error;
      }
    },
    extractVisits: async (startDate, endDate) => {
      try {
        const result = await axiosInst.get(`/visits/export?`, {
          params: { startDate, endDate },
          responseType: "blob",
        });

        return result.data;
      } catch (error) {
        console.error("Failed to download status", { message: error.message });
        throw error;
      }
    },
    analyseCode: async code => {
      try {
        const result = await axiosInst.get(`/qrcode/${code}?`);

        return result.data;
      } catch (error) {
        console.error("Failed to analyse code", { message: error.message });
        throw error;
      }
    },

    visits: {
      isValid: async (visitId, visitorId) => {
        try {
          const result = await axiosInst.get(
            `/visits/check?visitId=${visitId}&visitorId=${visitorId}`,
          );

          return result.data;
        } catch (error) {
          console.error("Failed to check visit", { message: error.message });
          throw error;
        }
      },
      search: async (criteria = {}, page = 1, size = 12) => {
        try {
          const { residentId, visitorId, from, to } = criteria;
          const result = await axiosInst.post(`/visits/search?page=${page}&size=${size}`, {
            hideCanceled: true,
            residentId,
            visitorId,
            from,
            to,
          });

          return result.data;
        } catch (error) {
          console.error("Failed to list visits", { message: error.message });
          throw error;
        }
      },
      create: async (startDate, endDate, visitors, resident, duration) => {
        try {
          const result = await axiosInst.post(`/visits?`, {
            startDate,
            endDate,
            resident,
            visitors,
            duration,
          });

          return result.data;
        } catch (error) {
          console.error("Failed to create visits", { message: error.message });
          throw error;
        }
      },
      update: async (visitId, startDate, endDate, visitors, resident) => {
        try {
          const result = await axiosInst.put(`/visits/${visitId}?`, {
            startDate,
            endDate,
            resident,
            visitors,
          });

          return result.data;
        } catch (error) {
          console.error("Failed to update visits", { message: error.message });
          throw error;
        }
      },
      cancel: async visitId => {
        try {
          const result = await axiosInst.patch(`/visits/${visitId}?`);

          return result.data;
        } catch (error) {
          console.error("Failed to update visits", { message: error.message });
          throw error;
        }
      },
    },
    users: {
      searchResident: async (search, page = 1, size = 10) => {
        try {
          const result = await axiosInst.get(
            `/residents?search=${search}&page=${page}&size=${size}`,
          );

          return result.data.items;
        } catch (error) {
          console.error("Failed to search residents", { message: error.message });
          throw error;
        }
      },
      searchVisitor: async (search, page = 1, size = 10) => {
        try {
          const result = await axiosInst.get(
            `/visitors?search=${search}&page=${page}&size=${size}`,
          );

          return result.data.items;
        } catch (error) {
          console.error("Failed to search visitors", { message: error.message });
          throw error;
        }
      },
      createVisitor: async ({ login, email, firstname, lastname, company, phone }) => {
        try {
          const result = await axiosInst.post(`/visitors?`, {
            login,
            email,
            firstname,
            lastname,
            company,
            phone,
          });

          return result.data;
        } catch (error) {
          console.error("Failed to create visitor", { message: error.message });
          throw error;
        }
      },
      getEphemeralCode: async ({ userId }) => {
        try {
          const result = await axiosInst.get(`/qrcode/${userId}/ephemeral?`);

          return result.data;
        } catch (error) {
          console.error("Failed to get ephemeral code", { message: error.message });
          throw error;
        }
      },
      listVisitorsHistory: async search => {
        try {
          const result = await axiosInst.get(`/visitors/history?search=${search}`);

          return result.data;
        } catch (error) {
          console.error("Failed to get visitors history", { message: error.message });
          throw error;
        }
      },
    },
    badges: {
      create: async (visitId, visitorId) => {
        try {
          const result = await axiosInst.post(`/badges?`, {
            visitId,
            visitorId,
          });

          return result.data;
        } catch (error) {
          console.error("Failed to create badge", { message: error.message });
          throw error;
        }
      },
      getZPL: async badgeContent => {
        try {
          const result = await axiosInst.get(`/badges/${badgeContent}?`);

          return result.data;
        } catch (error) {
          console.error("Failed to get getZPL", { message: error.message });
          throw error;
        }
      },
      checkIn: async badgeContent => {
        try {
          const result = await axiosInst.patch(`/badges/${badgeContent}?status=entry`);

          return result.data;
        } catch (error) {
          console.error("Failed to checkin", { message: error.message });
          throw error;
        }
      },
      checkOut: async badgeContent => {
        try {
          const result = await axiosInst.patch(`/badges/${badgeContent}?status=exit`);

          return result.data;
        } catch (error) {
          console.error("Failed to checkout", { message: error.message });
          throw error;
        }
      },
      keyCard: {
        associate: async (badgeContent, keycard) => {
          try {
            const result = await axiosInst.put(`/keycard?keycard=${keycard}&badge=${badgeContent}`);

            return result.data;
          } catch (error) {
            console.error("Failed to associate", { message: error.message });
            throw error;
          }
        },
        release: async (keycards, markAsExited) => {
          try {
            const result = await axiosInst.delete(
              `/keycard?keycards=${keycards}&markAsExited=${markAsExited}`,
            );

            return result.data;
          } catch (error) {
            console.error("Failed to release", { message: error.message });
            throw error;
          }
        },
        getOwner: async badgeRef => {
          try {
            const result = await axiosInst.get(`/keycard?reference=${badgeRef}`);

            return result.data;
          } catch (error) {
            console.error("Failed to get owner", { message: error.message });
            throw error;
          }
        },
        list: async (search, page, size, onlyWarning, hideLost) => {
          try {
            const result = await axiosInst.get(
              `/keycard/list?page=${page}&size=${size}&search=${search}&onlyWarning=${onlyWarning}&hideLost=${hideLost}`,
            );

            return result.data;
          } catch (error) {
            console.error("Failed to get owner", { message: error.message });
            throw error;
          }
        },
        updateNotes: async (keycard, notes) => {
          try {
            const result = await axiosInst.patch(`/keycard?keycard=${keycard}`, { notes });

            return result.data;
          } catch (error) {
            console.error("Failed to update keycard note", { message: error.message });
            throw error;
          }
        },
        lost: async keycard => {
          try {
            const result = await axiosInst.patch(`/keycard?keycard=${keycard}`, {
              badgeLost: true,
            });

            return result.data;
          } catch (error) {
            console.error("Failed to update keycard lost", { message: error.message });
            throw error;
          }
        },
      },
    },
  };
};

export default api;
